import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EntityPicker } from "@/components/app/EntityPicker";
import { AddressSelect } from "@/components/order/AddressSelect";
import { useFetch } from "@/hooks/useFetch";
import { MultiPick, uploadFiles } from "@/components/order/ServiceReports";
import { api, errMsg } from "@/lib/api";
import { inr, today } from "@/lib/format";

const lmRender = (o) => ({ title: o.order_no, subtitle: `${o.customer_snapshot?.name} · PO ${o.customer_po_no || "—"}` });

function Lines({ orderId, qty, setQty, value, setValue }) {
  const { data } = useFetch(`/orders/${orderId}/invoice-lines`);
  if (!data) return null;
  if (!data.length) return (
    <div className="grid grid-cols-2 gap-3" data-testid="inv-value-only">
      <p className="col-span-2 text-xs text-slate-500">This LM has no line items, so invoice it by value.</p>
      <div><Label>Basic value (without GST) *</Label><Input type="number" min={0} value={value.basic} onChange={(e) => setValue({ ...value, basic: e.target.value })} data-testid="inv-basic-value" /></div>
      <div><Label>GST %</Label><Input type="number" min={0} max={100} value={value.tax} onChange={(e) => setValue({ ...value, tax: e.target.value })} data-testid="inv-tax-pct" /></div>
    </div>
  );
  const basic = data.reduce((s, l) => s + Number(qty[l.id] || 0) * l.unit_price, 0);
  const gst = data.reduce((s, l) => s + (Number(qty[l.id] || 0) * l.unit_price * (l.tax_pct ?? 18)) / 100, 0);
  return (
    <table className="w-full text-xs" data-testid="inv-lines">
      <thead className="bg-slate-50 text-[10px] uppercase text-slate-500"><tr>{["Line", "Rate", "Left to invoice", "Invoice qty", "Basic"].map((x) => <th key={x} className="px-2 py-1 text-left">{x}</th>)}</tr></thead>
      <tbody>{data.map((l) => {
        const q = Number(qty[l.id] || 0); const over = q > l.left_to_invoice + 1e-9;
        return (<tr key={l.id} className="border-b border-slate-100">
          <td className="px-2 py-1 max-w-[240px] truncate">{l.line_no}. {l.description}</td><td className="px-2 font-mono">{inr(l.unit_price)}</td>
          <td className="px-2 font-mono">{l.left_to_invoice} {l.uom}</td>
          <td className="px-1 py-1 w-28"><Input type="number" min={0} disabled={l.left_to_invoice <= 0} className={`h-8 ${over ? "border-red-500 bg-red-50" : ""}`} value={qty[l.id] || ""} onChange={(e) => setQty({ ...qty, [l.id]: e.target.value })} data-testid={`inv-qty-${l.line_no}`} /></td>
          <td className="px-2 font-mono">{inr(q * l.unit_price)}</td></tr>);
      })}<tr><td colSpan={4} className="px-2 py-1 text-right font-semibold">Basic value</td><td className="px-2 font-mono font-semibold" data-testid="inv-basic-total">{inr(basic)}</td></tr>
        <tr><td colSpan={4} className="px-2 py-1 text-right text-slate-600">GST · Total incl. GST</td><td className="px-2 font-mono" data-testid="inv-gross-total">{inr(gst)} · {inr(basic + gst)}</td></tr></tbody>
    </table>
  );
}

export function NewInvoiceDialog({ presetOrder, onClose, onSaved }) {
  const [order, setOrder] = useState(presetOrder || null);
  const [f, setF] = useState({ invoice_no: "", invoice_date: today(), notes: "" });
  const [addr, setAddr] = useState(null);
  const [qty, setQty] = useState({});
  const [value, setValue] = useState({ basic: "", tax: 18 });
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState([]);
  const save = async () => {
    setBusy(true);
    try {
      const lines = Object.entries(qty).filter(([, v]) => Number(v) > 0).map(([id, v]) => ({ line_item_id: id, qty: Number(v) }));
      const r = await api.post("/invoices", { ...f, order_id: order.id, dispatch_address_id: addr, lines, basic_value: value.basic ? Number(value.basic) : null, tax_pct: Number(value.tax) });
      toast.success(`Invoice ${r.data.invoice_no} saved`);
      if (files.length) {
        try { await uploadFiles(files, { entity_type: "order", entity_id: order.id, doc_type: "Invoice", title: "", invoice_id: r.data.id }); toast.success(`${files.length} invoice file(s) uploaded`); }
        catch (e) { toast.error(`Invoice saved, but the file upload failed: ${errMsg(e)} - upload it from the LM Attachments tab`); }
      }
      onSaved?.(r.data); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="new-invoice-dialog">
        <DialogHeader><DialogTitle>New invoice{order ? ` · ${order.order_no}` : ""}</DialogTitle></DialogHeader>
        {!presetOrder && <EntityPicker url="/orders?status=approved,in_production,partially_dispatched,dispatched,delivered&is_projection=false" value={order} onChange={(o) => { setOrder(o); setQty({}); setAddr(null); }} render={lmRender} placeholder="Select the LM to invoice" testid="inv-lm-picker" />}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div><Label>Invoice number *</Label><Input value={f.invoice_no} onChange={(e) => setF({ ...f, invoice_no: e.target.value })} placeholder="e.g. TS/26-27/0412" data-testid="inv-no" /></div>
          <div><Label>Invoice date *</Label><Input type="date" value={f.invoice_date} onChange={(e) => setF({ ...f, invoice_date: e.target.value })} data-testid="inv-date" /></div>
          <div><Label>Notes</Label><Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="inv-notes" /></div>
        </div>
        {order && <><AddressSelect key={order.id} orderId={order.id} value={addr} onChange={setAddr} testid="inv-address" />
          <Lines key={`l${order.id}`} orderId={order.id} qty={qty} setQty={setQty} value={value} setValue={setValue} />
          <div data-testid="inv-upload"><Label>Upload invoice (optional - PDF, scan or photo; several files allowed)</Label><div className="mt-1"><MultiPick files={files} setFiles={setFiles} testid="inv-file" /></div></div></>}
        <DialogFooter><Button onClick={save} disabled={busy || !order || !f.invoice_no.trim() || !addr} data-testid="inv-save-btn">{busy ? "Saving…" : "Save invoice"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
